import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Environment, CreateEnvironmentRequest, UpdateEnvironmentRequest, Definition, DataBuild } from '../types';
import networkManager from '../services/NetworkManager';
import { useModal } from '../contexts/ModalContext';
import { ModalActions, ModalFormField, SearchableDropdown } from '../components/ModalComponents';

// Create Environment Form Component
const CreateEnvironmentForm: React.FC = () => {
  const { closeModal } = useModal();
  const [formData, setFormData] = useState<CreateEnvironmentRequest>({
    name: '',
    description: '',
    DefinitionID: '',
    GamedataID: ''
  });
  
  // Definition search states
  const [createDefinitionSearchTerm, setCreateDefinitionSearchTerm] = useState('');
  const [selectedCreateDefinition, setSelectedCreateDefinition] = useState<Definition | null>(null);
  
  // Gamedata search states
  const [createGamedataSearchTerm, setCreateGamedataSearchTerm] = useState('');
  const [selectedCreateGamedata, setSelectedCreateGamedata] = useState<DataBuild | null>(null);

  // Load definitions and data builds
  const [definitions, setDefinitions] = useState<Definition[]>([]);
  const [dataBuilds, setDataBuilds] = useState<DataBuild[]>([]);
  const [loadingDefinitions, setLoadingDefinitions] = useState(false);
  const [loadingDataBuilds, setLoadingDataBuilds] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingDefinitions(true);
        setLoadingDataBuilds(true);
        
        const [defsData, buildsData] = await Promise.all([
          networkManager.getDefinitions(),
          networkManager.getDataBuilds()
        ]);
        
        const definitionsArray = Array.isArray(defsData) ? defsData : [];
        const successfulDataBuilds = Array.isArray(buildsData) 
          ? buildsData.filter(build => build.status === 'success')
          : [];
        
        setDefinitions(definitionsArray);
        setDataBuilds(successfulDataBuilds);
      } catch (err) {
        console.error('Error loading data:', err);
        setDefinitions([]);
        setDataBuilds([]);
      } finally {
        setLoadingDefinitions(false);
        setLoadingDataBuilds(false);
      }
    };
    
    loadData();
  }, []);

  const handleCreateEnvironment = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await networkManager.createEnvironment(formData);
      closeModal('create-environment');
      setFormData({ name: '', description: '', DefinitionID: '', GamedataID: '' });
      setSelectedCreateDefinition(null);
      setSelectedCreateGamedata(null);
      setCreateDefinitionSearchTerm('');
      setCreateGamedataSearchTerm('');
      window.location.reload();
    } catch (err) {
      console.error('Failed to create environment:', err);
    }
  };

  const handleCreateDefinitionSearch = (searchValue: string) => {
    setCreateDefinitionSearchTerm(searchValue);
    
    if (searchValue.trim() === '') {
      setSelectedCreateDefinition(null);
      setFormData(prev => ({ ...prev, DefinitionID: '' }));
      return;
    }

    const found = definitions.find(def => 
      def.title.toLowerCase().includes(searchValue.toLowerCase())
    );
    
    if (found) {
      setSelectedCreateDefinition(found);
      setFormData(prev => ({ ...prev, DefinitionID: found._id }));
    } else {
      setSelectedCreateDefinition(null);
      setFormData(prev => ({ ...prev, DefinitionID: '' }));
    }
  };

  const handleCreateDefinitionSelect = (definition: Definition) => {
    setSelectedCreateDefinition(definition);
    setCreateDefinitionSearchTerm(definition.title);
    setFormData(prev => ({ ...prev, DefinitionID: definition._id }));
  };

  const handleCreateGamedataSearch = (searchValue: string) => {
    setCreateGamedataSearchTerm(searchValue);
    
    if (searchValue.trim() === '') {
      setSelectedCreateDefinition(null);
      setFormData(prev => ({ ...prev, GamedataID: '' }));
      return;
    }

    const found = dataBuilds.find(build => 
      build.title.toLowerCase().includes(searchValue.toLowerCase())
    );
    
    if (found) {
      setSelectedCreateGamedata(found);
      setFormData(prev => ({ ...prev, GamedataID: found._id }));
    } else {
      setSelectedCreateGamedata(null);
      setFormData(prev => ({ ...prev, GamedataID: '' }));
    }
  };

  const handleCreateGamedataSelect = (gamedata: DataBuild) => {
    setSelectedCreateGamedata(gamedata);
    setCreateGamedataSearchTerm(gamedata.title);
    setFormData(prev => ({ ...prev, GamedataID: gamedata._id }));
  };

  const filteredCreateDefinitions = createDefinitionSearchTerm.trim() === '' 
    ? (definitions || []).slice(0, 5)
    : (definitions || []).filter(def =>
        def.title.toLowerCase().includes(createDefinitionSearchTerm.toLowerCase())
      ).slice(0, 5);

  const filteredCreateGamedata = createGamedataSearchTerm.trim() === '' 
    ? (dataBuilds || []).slice(0, 5)
    : (dataBuilds || []).filter(build =>
        build.title.toLowerCase().includes(createGamedataSearchTerm.toLowerCase())
      ).slice(0, 5);

  return (
    <form onSubmit={handleCreateEnvironment} className="space-y-6">
      <ModalFormField label="Name" required>
        <input
          type="text"
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter environment name"
        />
      </ModalFormField>

      <ModalFormField label="Description">
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          rows={3}
          placeholder="Enter description (optional)"
        />
      </ModalFormField>

      <SearchableDropdown
        label="Definition"
        value={createDefinitionSearchTerm}
        onChange={handleCreateDefinitionSearch}
        onFocus={() => {}}
        placeholder={loadingDefinitions ? "Loading definitions..." : "Search for a definition"}
        disabled={loadingDefinitions}
        loading={loadingDefinitions}
        items={filteredCreateDefinitions}
        onSelect={handleCreateDefinitionSelect}
        selectedItem={selectedCreateDefinition}
        onRemove={() => {
          setSelectedCreateDefinition(null);
          setCreateDefinitionSearchTerm('');
          setFormData(prev => ({ ...prev, DefinitionID: '' }));
        }}
      />

      <SearchableDropdown
        label="Gamedata"
        value={createGamedataSearchTerm}
        onChange={handleCreateGamedataSearch}
        onFocus={() => {}}
        placeholder={loadingDataBuilds ? "Loading gamedata..." : "Search for gamedata"}
        disabled={loadingDataBuilds}
        loading={loadingDataBuilds}
        items={filteredCreateGamedata}
        onSelect={handleCreateGamedataSelect}
        selectedItem={selectedCreateGamedata}
        onRemove={() => {
          setSelectedCreateGamedata(null);
          setCreateGamedataSearchTerm('');
          setFormData(prev => ({ ...prev, GamedataID: '' }));
        }}
      />

      <ModalActions
        actions={[
          {
            label: 'Cancel',
            onClick: () => {
              closeModal('create-environment');
            },
            variant: 'secondary'
          },
          {
            label: 'Create',
            type: 'submit',
            variant: 'primary'
          }
        ]}
      />
    </form>
  );
};

const EnvironmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  
  // Refs for create modal dropdowns
  const createDefinitionDropdownRef = useRef<HTMLDivElement>(null);
  const createGamedataDropdownRef = useRef<HTMLDivElement>(null);
  
  // Refs for update modal dropdowns
  const updateDefinitionDropdownRef = useRef<HTMLDivElement>(null);
  const updateGamedataDropdownRef = useRef<HTMLDivElement>(null);
  
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  const [selectedEnvironment, setSelectedEnvironment] = useState<Environment | null>(null);
  const [formData, setFormData] = useState<CreateEnvironmentRequest>({
    name: '',
    description: '',
    DefinitionID: '',
    GamedataID: ''
  });
  const [updateData, setUpdateData] = useState<UpdateEnvironmentRequest>({});
  
  // Definition search states for create modal
  const [createDefinitionSearchTerm, setCreateDefinitionSearchTerm] = useState('');
  const [selectedCreateDefinition, setSelectedCreateDefinition] = useState<Definition | null>(null);
  const [showCreateDefinitionDropdown, setShowCreateDefinitionDropdown] = useState(false);
  
  // Gamedata search states for create modal
  const [createGamedataSearchTerm, setCreateGamedataSearchTerm] = useState('');
  const [selectedCreateGamedata, setSelectedCreateGamedata] = useState<DataBuild | null>(null);
  const [showCreateGamedataDropdown, setShowCreateGamedataDropdown] = useState(false);
  
  // Definition search states for update modal
  const [definitions, setDefinitions] = useState<Definition[]>([]);
  const [loadingDefinitions, setLoadingDefinitions] = useState(false);
  const [definitionSearchTerm, setDefinitionSearchTerm] = useState('');
  const [selectedDefinition, setSelectedDefinition] = useState<Definition | null>(null);
  const [showDefinitionDropdown, setShowDefinitionDropdown] = useState(false);
  
  // Gamedata search states for update modal
  const [dataBuilds, setDataBuilds] = useState<DataBuild[]>([]);
  const [loadingDataBuilds, setLoadingDataBuilds] = useState(false);
  const [gamedataSearchTerm, setGamedataSearchTerm] = useState('');
  const [selectedGamedata, setSelectedGamedata] = useState<DataBuild | null>(null);
  const [showGamedataDropdown, setShowGamedataDropdown] = useState(false);

  useEffect(() => {
    const loadAllData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchEnvironments(),
          loadDefinitions(),
          loadDataBuilds()
        ]);
        setDataLoaded(true);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadAllData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (createDefinitionDropdownRef.current && !createDefinitionDropdownRef.current.contains(event.target as Node)) {
        setShowCreateDefinitionDropdown(false);
      }
      if (createGamedataDropdownRef.current && !createGamedataDropdownRef.current.contains(event.target as Node)) {
        setShowCreateGamedataDropdown(false);
      }
      
      if (updateDefinitionDropdownRef.current && !updateDefinitionDropdownRef.current.contains(event.target as Node)) {
        setShowDefinitionDropdown(false);
      }
      if (updateGamedataDropdownRef.current && !updateGamedataDropdownRef.current.contains(event.target as Node)) {
        setShowGamedataDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchEnvironments = async () => {
    try {
      const data = await networkManager.getEnvironments();
      setEnvironments(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch environments');
    }
  };

  const loadDefinitions = async () => {
    try {
      setLoadingDefinitions(true);
      const data = await networkManager.getDefinitions();
      const definitionsArray = Array.isArray(data) ? data : [];
      setDefinitions(definitionsArray);

    } catch (err) {
      console.error('Error loading definitions:', err);
      setDefinitions([]);
    } finally {
      setLoadingDefinitions(false);
    }
  };

  const loadDataBuilds = async () => {
    try {
      setLoadingDataBuilds(true);
      const data = await networkManager.getDataBuilds();
      const successfulDataBuilds = Array.isArray(data) 
        ? data.filter(build => build.status === 'success')
        : [];
      setDataBuilds(successfulDataBuilds);

    } catch (err) {
      console.error('Error loading data builds:', err);
      setDataBuilds([]);
    } finally {
      setLoadingDataBuilds(false);
    }
  };

  const handleCreateEnvironment = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await networkManager.createEnvironment(formData);
      closeModal('create-environment');
      setFormData({ name: '', description: '', DefinitionID: '', GamedataID: '' });
      setSelectedCreateDefinition(null);
      setSelectedCreateGamedata(null);
      setCreateDefinitionSearchTerm('');
      setCreateGamedataSearchTerm('');
      fetchEnvironments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create environment');
    }
  };

  const handleCreateDefinitionSearch = (searchValue: string) => {
    setCreateDefinitionSearchTerm(searchValue);
    setShowCreateDefinitionDropdown(true);
    
    if (searchValue.trim() === '') {
      setSelectedCreateDefinition(null);
      setFormData(prev => ({ ...prev, DefinitionID: '' }));
      return;
    }

    // Find definition by title (case-insensitive)
    const found = definitions.find(def => 
      def.title.toLowerCase().includes(searchValue.toLowerCase())
    );
    
    if (found) {
      setSelectedCreateDefinition(found);
      setFormData(prev => ({ ...prev, DefinitionID: found._id }));
    } else {
      setSelectedCreateDefinition(null);
      setFormData(prev => ({ ...prev, DefinitionID: '' }));
    }
  };

  const handleCreateDefinitionSelect = (definition: Definition) => {
    setSelectedCreateDefinition(definition);
    setCreateDefinitionSearchTerm(definition.title);
    setFormData(prev => ({ ...prev, DefinitionID: definition._id }));
    setShowCreateDefinitionDropdown(false);
  };

  const handleCreateGamedataSearch = (searchValue: string) => {
    setCreateGamedataSearchTerm(searchValue);
    setShowCreateGamedataDropdown(true);
    
    if (searchValue.trim() === '') {
      setSelectedCreateGamedata(null);
      setFormData(prev => ({ ...prev, GamedataID: '' }));
      return;
    }

    // Find gamedata by title (case-insensitive)
    const found = dataBuilds.find(build => 
      build.title.toLowerCase().includes(searchValue.toLowerCase())
    );
    
    if (found) {
      setSelectedCreateGamedata(found);
      setFormData(prev => ({ ...prev, GamedataID: found._id }));
    } else {
      setSelectedCreateGamedata(null);
      setFormData(prev => ({ ...prev, GamedataID: '' }));
    }
  };

  const handleCreateGamedataSelect = (gamedata: DataBuild) => {
    setSelectedCreateGamedata(gamedata);
    setCreateGamedataSearchTerm(gamedata.title);
    setFormData(prev => ({ ...prev, GamedataID: gamedata._id }));
    setShowCreateGamedataDropdown(false);
  };

  const handleUpdateEnvironment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEnvironment) return;
    
    try {
      await networkManager.updateEnvironment(selectedEnvironment.name, updateData);
      closeModal('update-environment');
      setSelectedEnvironment(null);
      setUpdateData({});
      setSelectedDefinition(null);
      setSelectedGamedata(null);
      setDefinitionSearchTerm('');
      setGamedataSearchTerm('');
      fetchEnvironments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update environment');
    }
  };

  const openUpdateModal = async (environment: Environment) => {
    setSelectedEnvironment(environment);
    
    // Load definitions and data builds for search
    await Promise.all([loadDefinitions(), loadDataBuilds()]);
    
    if (environment.publishedDefinitionID) {
      const definition = definitions.find(def => def._id === environment.publishedDefinitionID);
      if (definition) {
        setSelectedDefinition(definition);
        setDefinitionSearchTerm(definition.title);
        setUpdateData(prev => ({ ...prev, DefinitionID: definition._id }));
      }
    }
    
    if (environment.publishedDataID) {
      const gamedata = dataBuilds.find(build => build._id === environment.publishedDataID);
      if (gamedata) {
        setSelectedGamedata(gamedata);
        setGamedataSearchTerm(gamedata.title);
        setUpdateData(prev => ({ ...prev, GamedataID: gamedata._id }));
      }
    }
    
    const updateModalContent = (
      <form onSubmit={handleUpdateEnvironment} className="space-y-6">
        <SearchableDropdown
          label="Definition"
          value={definitionSearchTerm}
          onChange={handleDefinitionSearch}
          onFocus={() => setShowDefinitionDropdown(true)}
          placeholder={loadingDefinitions ? "Loading definitions..." : "Search for a definition"}
          disabled={loadingDefinitions}
          loading={loadingDefinitions}
          items={filteredDefinitions}
          onSelect={handleDefinitionSelect}
          selectedItem={selectedDefinition}
          onRemove={() => {
            setSelectedDefinition(null);
            setDefinitionSearchTerm('');
            setUpdateData(prev => ({ ...prev, DefinitionID: '' }));
          }}
        />

        <SearchableDropdown
          label="Gamedata"
          value={gamedataSearchTerm}
          onChange={handleGamedataSearch}
          onFocus={() => setShowGamedataDropdown(true)}
          placeholder={loadingDataBuilds ? "Loading gamedata..." : "Search for gamedata"}
          disabled={loadingDataBuilds}
          loading={loadingDataBuilds}
          items={filteredGamedata}
          onSelect={handleGamedataSelect}
          selectedItem={selectedGamedata}
          onRemove={() => {
            setSelectedGamedata(null);
            setGamedataSearchTerm('');
            setUpdateData(prev => ({ ...prev, GamedataID: '' }));
          }}
        />

        <ModalActions
          actions={[
            {
              label: 'Cancel',
              onClick: () => {
                closeModal('update-environment');
                setSelectedEnvironment(null);
                setUpdateData({});
                setSelectedDefinition(null);
                setSelectedGamedata(null);
                setDefinitionSearchTerm('');
                setGamedataSearchTerm('');
              },
              variant: 'secondary'
            },
            {
              label: 'Update',
              onClick: () => handleUpdateEnvironment({ preventDefault: () => {} } as any),
              variant: 'primary',
              disabled: !updateData.DefinitionID && !updateData.GamedataID
            }
          ]}
        />
      </form>
    );

    openModal({
      id: 'update-environment',
      title: `Update Environment: ${environment.name}`,
      content: updateModalContent,
      size: 'lg'
    });
  };

  const openCreateModal = async () => {
    await Promise.all([loadDefinitions(), loadDataBuilds()]);
    
    const createModalContent = <CreateEnvironmentForm />;

    openModal({
      id: 'create-environment',
      title: 'Create Environment',
      content: createModalContent,
      size: 'lg'
    });
  };

  const handleDefinitionSearch = (searchValue: string) => {
    setDefinitionSearchTerm(searchValue);
    setShowDefinitionDropdown(true);
    
    if (searchValue.trim() === '') {
      setSelectedDefinition(null);
      setUpdateData(prev => ({ ...prev, DefinitionID: '' }));
      return;
    }

    // Find definition by title (case-insensitive)
    const found = definitions.find(def => 
      def.title.toLowerCase().includes(searchValue.toLowerCase())
    );
    
    if (found) {
      setSelectedDefinition(found);
      setUpdateData(prev => ({ ...prev, DefinitionID: found._id }));
    } else {
      setSelectedDefinition(null);
      setUpdateData(prev => ({ ...prev, DefinitionID: '' }));
    }
  };

  const handleDefinitionSelect = (definition: Definition) => {
    setSelectedDefinition(definition);
    setDefinitionSearchTerm(definition.title);
    setUpdateData(prev => ({ ...prev, DefinitionID: definition._id }));
    setShowDefinitionDropdown(false);
  };

  const handleGamedataSearch = (searchValue: string) => {
    setGamedataSearchTerm(searchValue);
    setShowGamedataDropdown(true);
    
    if (searchValue.trim() === '') {
      setSelectedGamedata(null);
      setUpdateData(prev => ({ ...prev, GamedataID: '' }));
      return;
    }

    // Find gamedata by title (case-insensitive)
    const found = dataBuilds.find(build => 
      build.title.toLowerCase().includes(searchValue.toLowerCase())
    );
    
    if (found) {
      setSelectedGamedata(found);
      setUpdateData(prev => ({ ...prev, GamedataID: found._id }));
    } else {
      setSelectedGamedata(null);
      setUpdateData(prev => ({ ...prev, GamedataID: '' }));
    }
  };

  const handleGamedataSelect = (gamedata: DataBuild) => {
    setSelectedGamedata(gamedata);
    setGamedataSearchTerm(gamedata.title);
    setUpdateData(prev => ({ ...prev, GamedataID: gamedata._id }));
    setShowGamedataDropdown(false);
  };

  const filteredDefinitions = definitionSearchTerm.trim() === '' 
    ? (definitions || []).slice(0, 5)
    : (definitions || []).filter(def =>
        def.title.toLowerCase().includes(definitionSearchTerm.toLowerCase())
      ).slice(0, 5);

  const filteredGamedata = gamedataSearchTerm.trim() === '' 
    ? (dataBuilds || []).slice(0, 5)
    : (dataBuilds || []).filter(build =>
        build.title.toLowerCase().includes(gamedataSearchTerm.toLowerCase())
      ).slice(0, 5);

  const getDefinitionTitle = (definitionId: string) => {
    if (!dataLoaded) return definitionId;
    const definition = definitions.find(def => def._id === definitionId);
    return definition ? definition.title : definitionId;
  };

  const getGamedataTitle = (gamedataId: string) => {
    if (!dataLoaded) return gamedataId;
    const gamedata = dataBuilds.find(build => build._id === gamedataId);
    return gamedata ? gamedata.title : gamedataId;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Environments</h1>
            <p className="text-gray-600 mt-1">Manage your deployment environments</p>
          </div>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 border border-blue-600 hover:border-blue-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 transform hover:scale-105"
          >
              <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
            Create Environment
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Definition
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Gamedata
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Definition Publisher
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data Publisher
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {environments.map((environment) => (
                <tr key={environment._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <Link
                      to={`/environments/${environment._id}`}
                      className="text-blue-600 hover:text-blue-900 hover:underline"
                    >
                      {environment.name}
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {environment.description || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {environment.publishedDefinitionID ? (
                      <Link
                        to={`/definitions/${environment.publishedDefinitionID}`}
                        className="text-blue-600 hover:text-blue-900 hover:underline"
                      >
                        {getDefinitionTitle(environment.publishedDefinitionID)}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {environment.publishedDataID ? (
                      <Link
                        to={`/data-builds/${environment.publishedDataID}`}
                        className="text-blue-600 hover:text-blue-900 hover:underline"
                      >
                        {getGamedataTitle(environment.publishedDataID)}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {environment.definitionPublisher?.name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {environment.dataPublisher?.name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(environment.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => openUpdateModal(environment)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentsPage;
